<template>
  <div class="admin-homepage">
    <div class="page-container">
      <div class="page-header">
        <div class="left-section">
          <Button
            label="Назад"
            icon="pi pi-arrow-left"
            class="back-btn"
            outlined
            @click="goBack"
          />
          <div>
            <h1 class="page-title">Главная страница</h1>
          </div>
        </div>
        <div class="header-actions">
          <Button
            v-if="canEditCarousel"
            label="Управлять порядком"
            icon="pi pi-arrow-up-down"
            severity="secondary"
            outlined
            :disabled="store.sortedCarouselItems.length < 2"
            @click="openReorderDialog"
          />
          <Button
            v-if="canEditCarousel"
            label="Добавить слайд"
            icon="pi pi-plus"
            @click="openCreateDialog"
          />
        </div>
      </div>

      <div class="carousel-preview-container">
        <div class="preview-toolbar">
          <div class="preview-info">
            <i class="pi pi-eye"></i>
            <span>Превью карусели</span>
          </div>
          <div class="slide-counter">
            <i class="pi pi-images"></i>
            <span
              >Слайд {{ currentSlideIndex + 1 }} из
              {{ store.sortedCarouselItems.length || 0 }}</span
            >
          </div>
        </div>

        <div
          v-if="store.sortedCarouselItems.length === 0 && !store.isLoading"
          class="empty-state"
        >
          <div class="empty-icon">
            <i class="pi pi-images"></i>
          </div>
          <h3>Нет слайдов в карусели</h3>
          <p>Добавьте первый слайд, и он сразу появится на главной странице</p>
          <Button
            v-if="canEditCarousel"
            label="Добавить слайд"
            icon="pi pi-plus"
            @click="openCreateDialog"
          />
        </div>

        <div
          v-else-if="store.sortedCarouselItems.length > 0"
          class="carousel-live-preview"
        >
          <Carousel
            :value="store.sortedCarouselItems"
            v-model:page="currentSlideIndex"
            :numVisible="1"
            :numScroll="1"
            :circular="true"
            :showIndicators="true"
            :showNavigators="store.sortedCarouselItems.length > 1"
            class="live-carousel"
          >
            <template #item="{ data }">
              <div class="carousel-slide-full" @click="openEditDialog(data)">
                <div
                  class="slide-bg"
                  :style="{ backgroundImage: 'url(' + data.imageUrl + ')' }"
                ></div>
                <div class="slide-overlay"></div>
                <div
                  v-if="data.title?.trim() || data.subtitle || (data.link && data.buttonText)"
                  class="slide-content-wrapper"
                  :class="[
                    'position-' + (data.titlePosition || 'center'),
                    'align-' + (data.textAlign || 'center'),
                  ]"
                >
                  <div class="slide-content">
                    <h2
                      v-if="data.title?.trim()"
                      class="slide-title"
                      :style="{
                        fontSize: (data.titleSize || 42) + 'px',
                        color: data.titleColor || '#FFFFFF',
                      }"
                    >
                      {{ data.title }}
                    </h2>
                    <p
                      v-if="data.subtitle"
                      class="slide-subtitle"
                      :style="{
                        fontSize: (data.subtitleSize || 16) + 'px',
                        color: data.subtitleColor || '#E0E0E0',
                      }"
                    >
                      {{ data.subtitle }}
                    </p>
                    <Button
                      v-if="data.link && data.buttonText"
                      :label="data.buttonText"
                      class="slide-btn"
                      @click.stop="navigateToLink(data.link)"
                    />
                  </div>
                </div>
                <div v-if="canEditCarousel" class="slide-edit-hint">
                  <i class="pi pi-pencil"></i>
                  <span>Кликните для редактирования</span>
                </div>
              </div>
            </template>
          </Carousel>

          <div class="thumbnails-bar">
            <div class="thumbnails-title">
              <i class="pi pi-list"></i>
              <span>Слайды</span>
            </div>
            <div class="thumbnails-list">
              <div
                v-for="(item, index) in store.sortedCarouselItems"
                :key="item.id"
                class="thumbnail-card"
                :class="{ active: currentSlideIndex === index }"
                @click="currentSlideIndex = index"
              >
                <img :src="item.imageUrl" class="thumbnail-img" />
                <div class="thumbnail-overlay">
                  <span class="thumbnail-number">{{ index + 1 }}</span>
                  <span class="thumbnail-title">{{
                    truncateText(item.title, 25)
                  }}</span>
                </div>
                <div v-if="canEditCarousel" class="thumbnail-actions">
                  <Button
                    icon="pi pi-pencil"
                    severity="secondary"
                    rounded
                    text
                    size="small"
                    @click.stop="openEditDialog(item)"
                  />
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    rounded
                    text
                    size="small"
                    @click.stop="openDeleteDialog(item)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CarouselFormDialog
        v-model="showFormDialog"
        :item="selectedItem"
        :submitting="store.isLoading"
        @save="handleSave"
      />

      <DeleteConfirmDialog
        v-model="showDeleteDialog"
        :loading="deleting"
        :item-title="selectedItem?.title"
        message="Вы уверены, что хотите удалить этот слайд?"
        @confirm="handleDelete"
      />

      <ReorderDialog
        v-model="showReorderDialog"
        :items="store.sortedCarouselItems"
        header="Управление порядком слайдов"
        @save="handleReorder"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import Button from "primevue/button";
import Carousel from "primevue/carousel";
import { useHomepageStore } from "../store/homepage.store";
import CarouselFormDialog from "../components/CarouselFormDialog.vue";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog.vue";
import ReorderDialog from "../components/ReorderDialog.vue";
import { useRouter } from "vue-router";
import { usePermissions } from "@/shared/composables/usePermissions";

const store = useHomepageStore();
const toast = useToast();
const router = useRouter();
const { can } = usePermissions();
const canEditCarousel = computed(() => can("EditCarousel"));

const showFormDialog = ref(false);
const deleting = ref(false)
const showDeleteDialog = ref(false);
const showReorderDialog = ref(false);
const selectedItem = ref(null);
const currentSlideIndex = ref(0);

onMounted(async () => {
  await store.fetchAllCarouselItems();
});

function goBack() {
  router.back();
}

function showSuccess(message) {
  toast.add({
    severity: "success",
    summary: "Успешно",
    detail: message,
    life: 3000,
  });
}

function showError(message) {
  toast.add({
    severity: "error",
    summary: "Ошибка",
    detail: message,
    life: 5000,
  });
}

function openCreateDialog() {
  if (!canEditCarousel.value) return;
  selectedItem.value = null;
  showFormDialog.value = true;
}

function openEditDialog(item) {
  if (!canEditCarousel.value) return;
  selectedItem.value = { ...item };
  showFormDialog.value = true;
}

function openDeleteDialog(item) {
  if (!canEditCarousel.value) return;
  selectedItem.value = item;
  showDeleteDialog.value = true;
}

function openReorderDialog() {
  if (!canEditCarousel.value) return;
  showReorderDialog.value = true;
}

function navigateToLink(link) {
  if (link.startsWith("http")) {
    window.open(link, "_blank");
  } else if (link) {
    window.location.href = link;
  }
}

async function handleSave(item) {
  try {
    if (item.id) {
      await store.updateItem(item.id, item);
      showSuccess("Слайд обновлён");
    } else {
      await store.createItem(item);
      showSuccess("Слайд создан");
    }
    showFormDialog.value = false;
  } catch (error) {
    showError(error.message || "Не удалось сохранить");
  }
}

async function handleDelete() {
  if (deleting.value) return
  if (selectedItem.value) {
    deleting.value = true
    try {
      await store.deleteItem(selectedItem.value.id);
      showSuccess("Слайд удалён");
      if (currentSlideIndex.value >= store.sortedCarouselItems.length) {
        currentSlideIndex.value = Math.max(
          0,
          store.sortedCarouselItems.length - 1,
        );
      }
    } catch (error) {
      showError(error.message || "Не удалось удалить");
    } finally {
      deleting.value = false
      showDeleteDialog.value = false
      selectedItem.value = null
    }
  }
}

async function handleReorder(itemOrders) {
  try {
    await store.reorderItems(itemOrders);
    showSuccess("Порядок слайдов обновлён");
    showReorderDialog.value = false;
  } catch (error) {
    showError(error.message || "Не удалось изменить порядок");
  }
}

function truncateText(str, length) {
  if (!str) return "";
  return str.length > length ? str.slice(0, length) + "..." : str;
}
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  background: transparent;
  border-bottom: 1px solid #e4e7ec;
  margin-bottom: 24px;
}

.left-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}
.admin-homepage {
  min-height: 100vh;
  background: #f5f7fa;
}

.page-container {
  max-width: 1250px;
  margin: 0 auto;
  padding: 0 32px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  background: transparent;
  border-bottom: 1px solid #e4e7ec;
  margin-bottom: 24px;
}

.header-left {
  flex: 1;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 4px 0;
  letter-spacing: -0.02em;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.carousel-preview-container {
  width: 100%;
}

.preview-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 20px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e4e7ec;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.preview-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 13px;
}

.preview-info i {
  color: #c41217;
}

.slide-counter {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 13px;
  background: #f3f4f6;
  padding: 4px 12px;
  border-radius: 20px;
}

.empty-state {
  text-align: center;
  padding: 80px 40px;
  background: #ffffff;
  border-radius: 24px;
  border: 2px dashed #e4e7ec;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon i {
  font-size: 40px;
  color: #9ca3af;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 500;
  color: #1a1a2e;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px 0;
}

.carousel-live-preview {
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #e4e7ec;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.live-carousel {
  position: relative;
}

.carousel-slide-full {
  position: relative;
  height: 420px;
  cursor: pointer;
  overflow: hidden;
}

.slide-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: transform 0.3s ease;
}

.carousel-slide-full:hover .slide-bg {
  transform: scale(1.02);
}

.slide-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.35) 0%,
    rgba(0, 0, 0, 0.55) 100%
  );
}

.slide-content-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  z-index: 2;
}

.position-top {
  align-items: flex-start;
  padding-top: 80px;
}

.position-center {
  align-items: center;
}

.position-bottom {
  align-items: flex-end;
  padding-bottom: 80px;
}

.align-left {
  justify-content: flex-start;
  text-align: left;
}

.align-center {
  justify-content: center;
  text-align: center;
}

.align-right {
  justify-content: flex-end;
  text-align: right;
}

.slide-content {
  max-width: 650px;
  padding: 0 40px;
  width: 100%;
}

.slide-title {
  font-weight: 700;
  margin: 0 0 16px 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.slide-subtitle {
  margin: 0 0 24px 0;
  line-height: 1.5;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

.slide-btn {
  background: transparent !important;
  border: 2px solid white !important;
  color: white !important;
  border-radius: 44px !important;
  padding: 10px 32px !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  transition: all 0.2s ease !important;
  backdrop-filter: blur(4px);
}

.slide-btn:hover {
  background: white !important;
  color: #111 !important;
  transform: translateY(-2px);
}

.slide-edit-hint {
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  padding: 6px 12px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  z-index: 3;
}

.carousel-slide-full:hover .slide-edit-hint {
  opacity: 1;
}

:deep(.live-carousel .p-carousel-prev),
:deep(.live-carousel .p-carousel-next) {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: #1a1a2e;
  transition: all 0.2s;
  z-index: 10;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

:deep(.live-carousel .p-carousel-prev:hover),
:deep(.live-carousel .p-carousel-next:hover) {
  background: white;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

:deep(.live-carousel .p-carousel-prev) {
  left: 20px;
}

:deep(.live-carousel .p-carousel-next) {
  right: 20px;
}

:deep(.live-carousel .p-carousel-indicators) {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  z-index: 10;
  gap: 8px;
}

:deep(.live-carousel .p-carousel-indicator button) {
  background: rgba(255, 255, 255, 0.6);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  transition: all 0.2s;
}

:deep(.live-carousel .p-carousel-indicator.p-highlight button) {
  background: #c41217;
  width: 24px;
  border-radius: 12px;
}

.thumbnails-bar {
  background: #f8f9fc;
  padding: 16px 20px;
  border-top: 1px solid #e4e7ec;
}

.thumbnails-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.thumbnails-list {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.thumbnails-list::-webkit-scrollbar {
  height: 3px;
}

.thumbnails-list::-webkit-scrollbar-track {
  background: #e4e7ec;
  border-radius: 4px;
}

.thumbnails-list::-webkit-scrollbar-thumb {
  background: #c41217;
  border-radius: 4px;
}

.thumbnail-card {
  position: relative;
  flex-shrink: 0;
  width: 150px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.thumbnail-card.active {
  border-color: #c41217;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(196, 18, 23, 0.15);
}

.thumbnail-img {
  width: 100%;
  height: 80px;
  object-fit: cover;
}

.thumbnail-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 8px;
}

.thumbnail-number {
  font-size: 10px;
  font-weight: 600;
  color: #c41217;
}

.thumbnail-title {
  font-size: 11px;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.thumbnail-actions {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.thumbnail-card:hover .thumbnail-actions {
  opacity: 1;
}

:deep(.thumbnail-actions .p-button) {
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.6) !important;
  border: none !important;
}

@media (max-width: 1400px) {
  .page-container {
    max-width: 1200px;
  }
}

@media (max-width: 1200px) {
  .page-container {
    max-width: 1000px;
  }
}

@media (max-width: 992px) {
  .page-container {
    max-width: 100%;
    padding: 0 24px;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 0 16px;
  }

  .page-header {
    padding: 16px 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
  }

  .carousel-slide-full {
    height: 320px;
  }

  .slide-content {
    padding: 0 24px;
  }

  .slide-title {
    font-size: 28px !important;
  }

  .slide-subtitle {
    font-size: 14px !important;
  }

  .slide-btn {
    padding: 8px 24px !important;
    font-size: 13px !important;
  }

  .position-top {
    padding-top: 50px;
  }

  .position-bottom {
    padding-bottom: 50px;
  }

  .preview-toolbar {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .thumbnail-card {
    width: 110px;
  }

  .thumbnail-img {
    height: 60px;
  }
}
</style>
